import React, { ReactNode } from "react";
import classNames from "classnames";
import { Helmet } from "react-helmet";

import ChatSidebar from "@/components/chat/ChatSidebar";
import ChatToggle from "@/components/chat/ChatToggle";
import Footer from "@/components/footer";
import Header from "@/components/header";
import { cn } from "@/utils/cn";

const Layout = ({
  children,
  title,
  padded,
  full,
  bgClass,
  header = (
    <Header
      noBottomPadding={undefined}
      noRoundedBottom={undefined}
      bottomBanner={undefined}
    />
  ),
  noHeader,
  centered,
  extraFooter,
  noFooter = false,
}: {
  children: React.ReactNode;
  title?: string;
  padded?: boolean;
  full?: boolean;
  bgClass?: string;
  header?: React.ReactNode;
  noHeader?: boolean;
  centered?: boolean;
  extraFooter?: React.ReactNode;
  noFooter?: boolean;
}) => (
  // IMPORTANT: Footer must mount BEFORE main content, flex-col-reverse allows us to keep the footer
  // at the bottom of the page but mount it first.

  // The Footer contains portal containers (fda-disclaimer-mobile-portal, fda-disclaimer-desktop-portal)
  // that the FDA disclaimer component needs to render into. If Footer mounts after the main content,
  // there's a race condition where the FDA disclaimer component mounts first, doesn't find the portal
  // containers, and returns null. Even when the Footer later creates the containers, the FDA disclaimer
  // won't re-render unless the location changes.

  // By mounting Footer first with flex-col-reverse:
  // 1. Footer creates portal containers immediately in DOM
  // 2. FDA disclaimer component can find containers when it mounts
  // 3. Visual layout remains the same (footer at bottom)

  // DO NOT change this order or remove flex-col-reverse without ensuring the FDA disclaimer
  // still works when navigating from treatment plan pages to refills pages.

  <div className="flex flex-col-reverse">
    {!noFooter && <Footer />}
    <div
      className={classNames("min-h-screen flex flex-col", {
        [bgClass || ""]: !!bgClass,
        "bg-white": !bgClass,
      })}
    >
      {title && (
        <Helmet>
          <title>{title}</title>
        </Helmet>
      )}

      {!noHeader && header}

      <div
        className={`flex-grow flex flex-col ${
          padded ? "my-12 px-4 lg:px-0" : ""
        }`}
      >
        <div
          className={`mx-auto flex-grow flex flex-col w-full ${
            padded ? "max-w-6xl" : full ? "max-w-full" : "max-w-[1260px]"
          } ${centered ? "items-center" : ""}`}
        >
          {children}
        </div>
      </div>
      {extraFooter && extraFooter}
    </div>
    <ChatSidebar />
    <ChatToggle />
  </div>
);
const LayoutStickyFooter: React.FC<{
  children?: ReactNode;
  className?: string;
}> = ({ children, className }) => {
  return (
    <div
      className={cn(
        "bg-evvy-dark-beige w-full sticky bottom-0 z-10 py-3.5 items-center flex flex-col sm:flex-row justify-between px-4 md:px-16 lg:px-[90px]",
        className
      )}
    >
      {children}
    </div>
  );
};

Layout.StickyFooter = LayoutStickyFooter;

export default Layout;
